import React, { createContext, useContext, useReducer, useEffect } from 'react';

const CartContext = createContext();

// Cart actions
const CART_ACTIONS = {
  ADD_ITEM: 'ADD_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  UPDATE_QUANTITY: 'UPDATE_QUANTITY',
  CLEAR_CART: 'CLEAR_CART',
  LOAD_CART: 'LOAD_CART'
};

// Cart reducer
const cartReducer = (state, action) => {
  switch (action.type) {
    case CART_ACTIONS.ADD_ITEM:
      // Normalize pharmacyId for comparison
      const payloadPharmacyId = action.payload.pharmacyId?._id || action.payload.pharmacyId;
      
      const existingItem = state.items.find(item => {
        const itemPharmacyId = item.pharmacyId?._id || item.pharmacyId;
        return item._id === action.payload._id && itemPharmacyId === payloadPharmacyId;
      });
      
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item => {
            const itemPharmacyId = item.pharmacyId?._id || item.pharmacyId;
            return item._id === action.payload._id && itemPharmacyId === payloadPharmacyId
              ? { ...item, quantity: item.quantity + 1 }
              : item;
          })
        };
      } else {
        return {
          ...state,
          items: [...state.items, { ...action.payload, quantity: 1 }]
        };
      }

    case CART_ACTIONS.REMOVE_ITEM:
      return {
        ...state,
        items: state.items.filter(item => {
          const itemPharmacyId = item.pharmacyId?._id || item.pharmacyId;
          const payloadPharmacyId = action.payload.pharmacyId?._id || action.payload.pharmacyId;
          return !(item._id === action.payload._id && itemPharmacyId === payloadPharmacyId);
        })
      };

    case CART_ACTIONS.UPDATE_QUANTITY:
      return {
        ...state,
        items: state.items.map(item => {
          const itemPharmacyId = item.pharmacyId?._id || item.pharmacyId;
          const payloadPharmacyId = action.payload.pharmacyId?._id || action.payload.pharmacyId;
          return item._id === action.payload._id && itemPharmacyId === payloadPharmacyId
            ? { ...item, quantity: Math.max(0, action.payload.quantity) }
            : item;
        }).filter(item => item.quantity > 0)
      };

    case CART_ACTIONS.CLEAR_CART:
      return {
        ...state,
        items: []
      };

    case CART_ACTIONS.LOAD_CART:
      return {
        ...state,
        items: action.payload || []
      };

    default:
      return state;
  }
};

// Initial state
const initialState = {
  items: []
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        const cartData = JSON.parse(savedCart);
        dispatch({ type: CART_ACTIONS.LOAD_CART, payload: cartData });
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(state.items));
  }, [state.items]);

  // Cart actions
  const addToCart = (product) => {
    dispatch({ type: CART_ACTIONS.ADD_ITEM, payload: product });
  };

  const removeFromCart = (productId, pharmacyId) => {
    dispatch({ type: CART_ACTIONS.REMOVE_ITEM, payload: { _id: productId, pharmacyId } });
  };

  const updateQuantity = (productId, pharmacyId, quantity) => {
    dispatch({ type: CART_ACTIONS.UPDATE_QUANTITY, payload: { _id: productId, pharmacyId, quantity } });
  };

  const clearCart = () => {
    dispatch({ type: CART_ACTIONS.CLEAR_CART });
  };

  // Cart calculations
  const getTotalItems = () => {
    return state.items.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return state.items.reduce((total, item) => {
      const price = item.price?.nigeria || item.price || 0;
      return total + (price * item.quantity);
    }, 0);
  };

  const getItemsByPharmacy = () => {
    const pharmacyGroups = {};
    state.items.forEach(item => {
      const pharmacyId = item.pharmacyId?._id || item.pharmacyId;
      const pharmacyName = item.pharmacyId?.pharmacyName || item.pharmacy?.name || 'Unknown Pharmacy';
      
      if (!pharmacyGroups[pharmacyId]) {
        pharmacyGroups[pharmacyId] = {
          pharmacyId,
          pharmacyName,
          items: []
        };
      }
      pharmacyGroups[pharmacyId].items.push(item);
    });
    return Object.values(pharmacyGroups);
  };

  const value = {
    items: state.items,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotalItems,
    getTotalPrice,
    getItemsByPharmacy
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;
